/**
 * The one human checkpoint — PRD 02 §9, PRD 04 §4.1.
 *
 * Generating a character produces its **base reference**: the instance at the neutral
 * variant, in the active theme. It is the locked source every later variant renders *from*,
 * and the single most cost-sensitive artifact in the system — cheap to redo one reference
 * sheet, ruinously expensive to redo a story built on the wrong one.
 *
 * So there is exactly one gate here, and it is a creative choice the parent makes ("here's
 * your fox — right?"), not a second review. Mechanically it is still a hard precondition:
 * nothing else generates until the base reference is accepted.
 *
 * "Identity locked" is **derived**, never stored on the primitive. Because the instance id
 * hashes `primitiveVersion` and `themeId`, acceptance is inherently per version and per
 * theme: an identity edit bumps the version, addresses a new reference, and asks the parent
 * to meet the fox again.
 */
import { canonicalVariant } from './address.js';
import { ApprovalError, VariantNotAllowedError } from './errors.js';
/** The neutral variant `{}` — the base reference, and the anchor every axis varies away from. */
export function isNeutralVariant(variant) {
    return Object.keys(canonicalVariant(variant)).length === 0;
}
/** Only characters carry the human gate. A setting or prop locks as soon as its reference is ready. */
export function requiresApproval(primitive) {
    return primitive.kind === 'character';
}
/**
 * Why this primitive is not locked, or `null` when it is.
 *
 * Checked most-specific first: a stale request version is reported as such even when the
 * base is missing, because "you are asking for an old identity" is the actionable fact.
 */
export function identityLockRefusal(primitive, baseInstance, requestedVersion = primitive.version) {
    if (requestedVersion !== primitive.version)
        return 'version-mismatch';
    if (baseInstance === null)
        return 'no-base';
    if (baseInstance.primitiveVersion !== primitive.version)
        return 'version-mismatch';
    if (baseInstance.status !== 'ready')
        return 'base-not-ready';
    if (requiresApproval(primitive) && baseInstance.approved !== true)
        return 'base-not-approved';
    return null;
}
/**
 * Is this primitive's identity locked? `ready`, at the primitive's current version, and —
 * for a character — accepted by the parent. Never stored; always derived.
 */
export function isIdentityLocked(primitive, baseInstance) {
    return identityLockRefusal(primitive, baseInstance) === null;
}
/**
 * Refuse a non-neutral variant unless the primitive's identity is locked.
 *
 * A base-reference request is always allowed — that request *is* how the lock gets made.
 * Everything else renders *from* the locked reference, so without one there is nothing to
 * render from, and a refused request must cost nothing: this throws before any write.
 */
export function assertVariantAllowed(primitive, baseInstance, request) {
    if (isNeutralVariant(request.variant))
        return;
    const refusal = identityLockRefusal(primitive, baseInstance, request.primitiveVersion);
    if (refusal === null)
        return;
    throw new VariantNotAllowedError(primitive.id, refusal, `cannot resolve a variant of ${primitive.kind} ${primitive.id}: ${REFUSAL_MESSAGES[refusal]}`);
}
/**
 * Every character in a scene's cast must be locked before the scene renders. Same refusal
 * vocabulary as the variant gate, because it is the same gate one level up.
 */
export function assertCharacterApproved(primitive, baseInstance) {
    const refusal = identityLockRefusal(primitive, baseInstance);
    if (refusal === null)
        return;
    throw new VariantNotAllowedError(primitive.id, refusal, `cannot render a scene with ${primitive.kind} ${primitive.id}: ${REFUSAL_MESSAGES[refusal]}`);
}
const REFUSAL_MESSAGES = Object.freeze({
    'no-base': 'it has no base reference yet',
    'base-not-ready': 'its base reference is not ready',
    'base-not-approved': 'its base reference has not been accepted',
    'version-mismatch': 'the requested version is not the primitive current version',
});
/**
 * Accept a base reference — the parent meeting their character.
 *
 * Refuses anything that is not a ready base reference, so approval can never land on a
 * variant (which would lock the wrong picture) or on a record that has not drawn yet.
 * Pure: returns a new instance.
 */
export function approveBaseReference(instance, stamp) {
    if (!isNeutralVariant(instance.variant)) {
        throw new ApprovalError(`cannot approve ${instance.id}: approval belongs on the base reference (the neutral variant), not on a variant`);
    }
    if (instance.status !== 'ready') {
        throw new ApprovalError(`cannot approve ${instance.id}: status is ${instance.status}, not ready`);
    }
    return {
        ...instance,
        approved: true,
        approvedAt: stamp.at,
        approvedBy: stamp.by,
    };
}
//# sourceMappingURL=approval.js.map